/**
 * Normalize broker OHLC into a stable bar shape for the backtest engine.
 */

export function toUnixSeconds(timestamp) {
  if (timestamp == null) return null;
  if (typeof timestamp === 'number' && Number.isFinite(timestamp)) {
    return timestamp > 1e12 ? Math.floor(timestamp / 1000) : Math.floor(timestamp);
  }
  const ms = Date.parse(timestamp);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

export function toChartTime(timestamp, unit = 'Minute') {
  const u = String(unit || 'Minute');
  if (u === 'Daily' || u === 'Weekly' || u === 'Monthly') {
    const iso = typeof timestamp === 'string' ? timestamp : new Date((toUnixSeconds(timestamp) || 0) * 1000).toISOString();
    return String(iso).substring(0, 10);
  }
  return toUnixSeconds(timestamp);
}

function num(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

export function normalizeBar(raw, index = 0, unit = 'Minute') {
  if (!raw || typeof raw !== 'object') return null;
  const timestamp = raw.TimeStamp || raw.timestamp || raw.TradeTime || raw.time;
  const open = num(raw.Open ?? raw.open);
  const high = num(raw.High ?? raw.high);
  const low = num(raw.Low ?? raw.low);
  const close = num(raw.Close ?? raw.close);
  const volume = num(raw.TotalVolume ?? raw.Volume ?? raw.volume) || 0;
  const timeSec = toUnixSeconds(timestamp);
  const time = toChartTime(timestamp, unit);
  if (time == null || timeSec == null || [open, high, low, close].some((n) => n == null)) return null;
  return {
    index,
    time,
    timeSec,
    timestamp: typeof timestamp === 'string' ? timestamp : new Date(timeSec * 1000).toISOString(),
    open,
    high,
    low,
    close,
    volume,
  };
}

export function normalizeBars(rawBars, unit = 'Minute') {
  if (!Array.isArray(rawBars)) return [];
  const out = [];
  for (let i = 0; i < rawBars.length; i++) {
    const bar = normalizeBar(rawBars[i], out.length, unit);
    if (bar) out.push(bar);
  }
  out.sort((a, b) => a.timeSec - b.timeSec);
  const deduped = [];
  const seen = new Set();
  for (const bar of out) {
    const key = `${bar.timeSec}|${bar.open}|${bar.close}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push({ ...bar, index: deduped.length });
  }
  return deduped;
}

export function formatShortStamp(timestamp) {
  if (!timestamp) return '—';
  const d = new Date(typeof timestamp === 'number' ? timestamp * 1000 : timestamp);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatBarSpan(bars) {
  if (!Array.isArray(bars) || bars.length === 0) return 'No bars';
  const first = formatShortStamp(bars[0].timestamp || bars[0].timeSec);
  const count = bars.length.toLocaleString();
  const noun = bars.length === 1 ? 'bar' : 'bars';
  return `Earliest Bar ${first} · ${count} ${noun}`;
}

export function isoToDateInput(value) {
  if (!value) return '';
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function defaultRangeForUnit(unit, now = new Date()) {
  return rangeFromPreset(defaultRangePreset(unit), now);
}

export const FULL_RANGE_FIRSTDATE = '2010-01-01';

export const BACKTEST_RANGE_PRESETS = [
  { id: '1M', label: '1M', months: 1 },
  { id: '3M', label: '3M', months: 3 },
  { id: '6M', label: '6M', months: 6 },
  { id: 'YTD', label: 'YTD', ytd: true },
  { id: '1Y', label: '1Y', months: 12 },
];

export function defaultRangePreset(unit) {
  const u = String(unit || 'Minute');
  if (u === 'Daily' || u === 'Weekly' || u === 'Monthly') return '1Y';
  return '1M';
}

export function rangeFromPreset(presetId, now = new Date()) {
  const end = new Date(now);
  const start = new Date(now);
  const preset = BACKTEST_RANGE_PRESETS.find((p) => p.id === presetId) || BACKTEST_RANGE_PRESETS[0];
  if (preset.ytd) {
    start.setMonth(0, 1);
  } else {
    start.setMonth(start.getMonth() - (preset.months || 1));
  }
  return {
    firstdate: isoToDateInput(start.toISOString()),
    lastdate: isoToDateInput(end.toISOString()),
  };
}

export function minutesPerBar(interval, unit) {
  const n = Number(interval) || 1;
  const u = String(unit || 'Minute');
  if (u === 'Minute') return n;
  if (u === 'Hour' || u === 'Hourly') return n * 60;
  if (u === 'Daily') return n * 24 * 60;
  if (u === 'Weekly') return n * 7 * 24 * 60;
  if (u === 'Monthly') return n * 30 * 24 * 60;
  return n;
}

/** Conservative bars/day assuming a 24h session so page windows stay under the API cap. */
export function estimatedBarsPerDay(interval, unit) {
  const minutes = minutesPerBar(interval, unit);
  if (minutes >= 24 * 60) return 1;
  return Math.ceil((24 * 60) / minutes);
}

export const MAX_BARS_PER_REQUEST = 50000;

export function maxCalendarDaysPerRequest(interval, unit) {
  const perDay = estimatedBarsPerDay(interval, unit);
  const days = Math.floor(MAX_BARS_PER_REQUEST / Math.max(1, perDay));
  return Math.max(1, days);
}

/** Cap lookback so 1m data does not try to download years of bars. */
export function maxLookbackDays(interval, unit) {
  const minutes = minutesPerBar(interval, unit);
  if (minutes <= 1) return 90;
  if (minutes <= 4) return 180;
  if (minutes < 24 * 60) return 365;
  return 365 * 5;
}

export function clampRangeToLookback(range, interval, unit, now = new Date()) {
  const firstdate = isoToDateInput(range?.firstdate);
  const lastdate = isoToDateInput(range?.lastdate) || isoToDateInput(now.toISOString());
  if (!firstdate) return { firstdate, lastdate };
  const maxDays = maxLookbackDays(interval, unit);
  const minFirst = isoToDateInput(new Date(Date.parse(`${lastdate}T12:00:00Z`) - maxDays * 86400000).toISOString());
  return {
    firstdate: firstdate < minFirst ? minFirst : firstdate,
    lastdate,
  };
}
